import { t } from "@web-monorepo/i18n/macro";
import * as logClient from "@classdojo/log-client";
import { DDSBodyText, DDSHeadlineText, DDSIconButton } from "@web-monorepo/dds";
import { IconArrowLeft, IconArrowRight, IconPause, IconPlay } from "@web-monorepo/dds-icons";
import { getImageSrc } from "@web-monorepo/static-assets";
import { clsx } from "clsx";
import { useRef, useState } from "react";
import Lottie from "react-lottie";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import ToolkitCard from "components/ToolkitCard";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { MUSIC } from "utils/beyondToolkitTools";
import animationData from "./note_wiggle.json";
import styles from "./index.module.css";
import { useMusic } from "./useMusic.vm";

const captionsSrc = "/static/media/music-channel.vtt";

type MusicProps = {
  di?: { useVm?: typeof useMusic };
};

function Music({ di }: MusicProps) {
  const useVm = di?.useVm ?? useMusic;
  const vm = useVm();

  const [channelSelected, setChannelSelected] = useState<Channel | null>(null);
  const [trackNumber, setTrackNumber] = useState(0);
  const [paused, setPaused] = useState(false);
  const playPauseLabel = paused ? t`Play` : t`Pause`;

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const { title, imgSrc } = MUSIC();

  const audioNode = useRef<HTMLAudioElement | null>(null);

  const openChannel = (index: number) => {
    const channelList = channels(true);
    logParentToolkitEvent({ eventName: `music.${channelList[index].loggingId}.tap` });
    setChannelSelected(channelList[index]);
    setPaused(false);
  };

  const nextTrack = () => {
    if (!channelSelected) return;
    setTrackNumber((trackNumber + 1) % channelSelected.tracks.length);
    setPaused(false);
  };

  const previousTrack = () => {
    if (!channelSelected) return;
    setTrackNumber((channelSelected.tracks.length + (trackNumber - 1)) % channelSelected.tracks.length);
    setPaused(false);
  };

  const togglePause = () => {
    if (paused) {
      audioNode.current?.play().catch(() => {
        logClient.sendMetrics([{ type: "increment", value: 1, metricName: "toolkit.media.audio.play.error" }]);
      });
    } else {
      audioNode.current?.pause();
    }
    setPaused(!paused);
  };

  return (
    <ToolkitAppScroller title={title} variant="sleepMusic" imgSrc={imgSrc}>
      {channelSelected == null ? (
        <div className={styles.grid}>
          {channels(true).map((channel, i) => (
            <button
              type="button"
              data-name={`beyond_toolkit_music:channel_${i}`}
              key={i}
              onClick={() => openChannel(i)}
              className={styles.channelButton}
            >
              <img className={styles.channelImage} src={channel.imgSrc} alt="" />
              <DDSBodyText>{channel.title}</DDSBodyText>
            </button>
          ))}
        </div>
      ) : (
        <ToolkitCard close={() => setChannelSelected(null)}>
          <DDSHeadlineText as="h2" level="md">
            {channelSelected.title}
          </DDSHeadlineText>
          <div className={styles.lottieWrap}>
            <div className={styles.lottieInner}>
              <div className={styles.lottie}>
                <Lottie options={defaultOptions} height={250} width={450} isStopped={false} isPaused={paused} />
              </div>
              <img
                alt=""
                src={getImageSrc("toolkit/public/static/images/radio.png")}
                className={clsx(
                  styles.radio,
                  paused && styles.radioPaused,
                  RADIO_SPEED_CLASS[channelSelected.loggingId],
                )}
              />
            </div>
          </div>
          <audio
            src={channelSelected.tracks[trackNumber].src}
            autoPlay={true}
            ref={audioNode}
            onPlay={vm.onAudioPlay}
            onEnded={nextTrack}
          >
            <track kind="captions" src={captionsSrc} srcLang="en" label={t`English`} default />
          </audio>
          <div className={styles.controls}>
            <DDSIconButton
              data-name="beyond_toolkit_music:previous_track"
              aria-label={t`Previous track`}
              tooltip={{
                content: t`Previous track`,
                dataName: "beyond_toolkit_music:previous_track:tooltip",
              }}
              variant="tertiary"
              onClick={previousTrack}
            >
              <IconArrowLeft />
            </DDSIconButton>
            <div className={styles.playPauseWrap}>
              <DDSIconButton
                data-name="beyond_toolkit_music:play_pause"
                aria-label={playPauseLabel}
                tooltip={{
                  content: playPauseLabel,
                  dataName: "beyond_toolkit_music:play_pause:tooltip",
                }}
                variant="primary"
                size="large"
                onClick={togglePause}
              >
                {paused ? <IconPlay /> : <IconPause />}
              </DDSIconButton>
            </div>
            <DDSIconButton
              data-name="beyond_toolkit_music:next_track"
              aria-label={t`Next track`}
              tooltip={{ content: t`Next track`, dataName: "beyond_toolkit_music:next_track:tooltip" }}
              variant="tertiary"
              onClick={nextTrack}
            >
              <IconArrowRight />
            </DDSIconButton>
          </div>
        </ToolkitCard>
      )}
    </ToolkitAppScroller>
  );
}

export default Music;

type ChannelId = "focus" | "active" | "calm" | "sleep";

type Channel = {
  title: string;
  imgSrc: string;
  tracks: { src: string }[];
  loggingId: ChannelId;
};

// Per-channel pulse-animation speed lives in `index.module.css` as
// `.radioFocus` / `.radioActive` / `.radioCalm` / `.radioSleep`.
const RADIO_SPEED_CLASS: Record<ChannelId, string> = {
  focus: styles.radioFocus,
  active: styles.radioActive,
  calm: styles.radioCalm,
  sleep: styles.radioSleep,
};

const channels = (
  isSleepMusicVariation = true, //currently defaulted to only bedtime music channels
): Channel[] =>
  (
    [
      {
        title: t`Focus`,
        imgSrc: getImageSrc("toolkit/public/static/images/radio_focus.png"),
        tracks: Array.from({ length: 5 }, (_, n) => ({
          src: `https://static.classdojo.com/toolkit-static/radio/calm_${n + 1}.mp3`, //focus tracks are named 'calm' ¯\_(ツ)_/¯
        })),
        loggingId: "focus",
      },
      {
        title: t`Active`,
        imgSrc: getImageSrc("toolkit/public/static/images/radio_active.png"),
        tracks: Array.from({ length: 5 }, (_, n) => ({
          src: `https://static.classdojo.com/toolkit-static/radio/upbeat_${n + 1}.mp3`,
        })),
        loggingId: "active",
      },
      {
        title: t`Calm`,
        imgSrc: getImageSrc("toolkit/public/static/images/radio_calm.png"),
        tracks: Array.from({ length: 5 }, (_, n) => ({
          src: `https://static.classdojo.com/toolkit-static/radio/beyond_calm_${n + 1}.mp3`,
        })),
        loggingId: "calm",
      },
      {
        title: t`Sleep`,
        imgSrc: getImageSrc("toolkit/public/static/images/radio_sleep.png"),
        tracks: Array.from({ length: 5 }, (_, n) => ({
          src: `https://static.classdojo.com/toolkit-static/radio/beyond_sleep_${n + 1}.mp3`,
        })),
        loggingId: "sleep",
      },
    ] as const satisfies readonly Channel[]
  ).slice(isSleepMusicVariation ? 2 : 0, 4);
